import React, { useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import * as XLSX from 'xlsx';
import Service from '../../../../services';
import { baseUrlAdmin } from '../../../../../store/apiConstants';

const UploadKeyDialog = ({ visible, onHide, exam, onSuccess }) => {
    const [uploading, setUploading] = useState(false);
    const toast = useRef();
    const service = new Service();

    // Prevent error if exam is not loaded yet
    if (!exam || !exam.subjects) {
        return (
            <Dialog header="Upload Key" visible={visible} style={{ width: '30vw' }} onHide={onHide}>
                <div>Loading exam data...</div>
            </Dialog>
        );
    }

    // Flatten all questions from all subjects
    const allQuestions = exam.subjects
        .flatMap(sub => sub.questions.map(q => ({
            qNo: q.qNo,
            questionType: q.questionType,
            subjectName: sub.subjectName
        })));

    // Download template for all questions
    const handleDownloadTemplate = () => {
        const data = allQuestions.map(q => ({
            'Question No': q.qNo,
            'Question Type': q.questionType,
            'Subject': q.subjectName,
            'Key': ''
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Keys');
        XLSX.writeFile(wb, `${exam.examName}_KeyTemplate.xlsx`);
    };

    // Handle upload for all questions
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) {
            toast.current.show({ severity: 'warn', summary: 'Select File', detail: 'Please select a file.' });
            return;
        }
        setUploading(true);
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet);

            // Prepare keys: qNo, questionType, key
            const keys = rows.map(row => ({
                qNo: row['Question No'],
                questionType: row['Question Type'],
                key: row['Key']
            }));

            // Use Service class for API call (like other components)
            await service.post(
                `${baseUrlAdmin}/examination/upload-offline-key`,
                {
                    examId: exam.examId,
                    keys
                },
                true // pass auth if needed
            );

            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Keys uploaded successfully.' });
            onSuccess && onSuccess();
            onHide();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to upload keys.' });
        }
        setUploading(false);
    };

    return (
        <Dialog
            header="Upload Answer Key"
            visible={visible}
            style={{ width: '35vw', maxWidth: 500 }}
            onHide={onHide}
            className="p-fluid"
        >
            <Toast ref={toast} />
            <div style={{ padding: '1.5rem 0' }}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <i className="pi pi-file-excel" style={{ fontSize: 40, color: '#1e88e5', marginBottom: 8 }} />
                    <h3 style={{ margin: '12px 0 4px 0' }}>{exam.examName}</h3>
                    <div style={{ color: '#888', fontSize: 14 }}>
                        Download the template, fill in the keys, and upload the file to update answer keys for all subjects.
                    </div>
                </div>
                <div className="p-field" style={{ marginBottom: 24 }}>
                    <Button
                        label="Download Template"
                        icon="pi pi-download"
                        className="p-button-rounded p-button-info p-mr-2"
                        onClick={handleDownloadTemplate}
                        style={{ width: '100%' }}
                    />
                </div>
                <div className="p-field" style={{ marginBottom: 24 }}>
                    <label htmlFor="key-upload" style={{ fontWeight: 500, marginBottom: 8, display: 'block' }}>
                        Upload Filled Key File
                    </label>
                    <input
                        id="key-upload"
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileUpload}
                        disabled={uploading}
                        style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6, background: '#fafbfc' }}
                    />
                </div>
                {uploading && (
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: 24, color: '#1e88e5' }} />
                        <span style={{ marginLeft: 8, color: '#1e88e5' }}>Uploading...</span>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default UploadKeyDialog;